import { Button, Form, Input, InputNumber, message } from 'antd';
import { useForm } from 'antd/es/form/Form';
import FormItem from 'antd/es/form/FormItem';
import FormList from 'antd/es/form/FormList';
import { DatePicker, Upload } from 'antd/lib';
import { ProviderSelectorContainer } from '../shared/provider-selector/provider-selector.containers';
import { read, utils } from 'xlsx';
import { find, map } from 'lodash';
import dayjs, { Dayjs } from 'dayjs';
import { useImperativeHandle } from 'react';
import { PlusOutlined } from '@ant-design/icons';

type EnterpriseOrderFormComponentProps = {
  formRef?: React.RefObject<any>;
};

export const EnterpriseOrderFormComponent: React.FC<
  EnterpriseOrderFormComponentProps
> = ({ formRef }) => {
  const [form] = useForm<{
    clientName: string;
    clientTaxId: string;
    contactInfo: string;
    deliveryAddress: string;
    deliveryDate: Dayjs;
    deliveryFee: number;
    discount: number;
    items: {
      providerId: string;
      name: string;
      quantity: number;
      price: number;
    }[];
  }>();

  useImperativeHandle(formRef, () => form, [form]);

  return (
    <Form form={form} preserve={false}>
      <div className="flex items-center mb-2 gap-2">
        <Upload
          accept=".xlsx"
          showUploadList={false}
          beforeUpload={(file) => {
            file
              .arrayBuffer()
              .then((buffer) => {
                const wb = read(buffer, {
                  type: 'array',
                  cellDates: true,
                });
                const [info, itemSheetName] = wb.SheetNames;

                if (info !== '訂單資訊') {
                  throw new Error('找不到訂單資訊，請上傳正確的檔案');
                }

                if (itemSheetName !== '訂單明細') {
                  throw new Error('找不到訂單明細，請上傳正確的檔案');
                }

                const orderInfo: any[][] = utils.sheet_to_json(
                  wb.Sheets[info],
                  {
                    header: 1,
                  }
                );

                const clientNameRow = find(
                  orderInfo,
                  (item: any[]) => item[0] === '客戶名稱'
                );

                if (!clientNameRow) {
                  throw new Error('找不到客戶名稱，請上傳正確的檔案');
                }

                const clientTaxIdRow = find(
                  orderInfo,
                  (item: any[]) => item[0] === '統一編號'
                );
                if (!clientTaxIdRow) {
                  throw new Error('找不到統一編號，請上傳正確的檔案');
                }
                const deliveryDateRow = find(
                  orderInfo,
                  (item: any[]) => item[0] === '訂餐日期'
                );
                if (!deliveryDateRow) {
                  throw new Error('找不到訂餐日期，請上傳正確的檔案');
                }
                const deliveryAddressRow = find(
                  orderInfo,
                  (item: any[]) => item[0] === '訂餐地址'
                );
                if (!deliveryAddressRow) {
                  throw new Error('找不到訂餐地址，請上傳正確的檔案');
                }
                const contactInfoRow = find(
                  orderInfo,
                  (item: any[]) => item[0] === '聯絡窗口'
                );
                if (!contactInfoRow) {
                  throw new Error('找不到聯絡窗口，請上傳正確的檔案');
                }
                const deliveryFeeRow = find(
                  orderInfo,
                  (item: any[]) => item[0] === '運費'
                );
                if (!deliveryFeeRow) {
                  throw new Error('找不到運費，請上傳正確的檔案');
                }
                const discountRow = find(
                  orderInfo,
                  (item: any[]) => item[0] === '折扣(%)'
                );
                if (!discountRow) {
                  throw new Error('找不到折扣，請上傳正確的檔案');
                }

                const clientName = clientNameRow[1];
                const clientTaxId = clientTaxIdRow[1];
                const deliveryDate = dayjs(deliveryDateRow[1]);
                const deliveryAddress = deliveryAddressRow[1];
                const contactInfo = contactInfoRow[1];
                const deliveryFee = deliveryFeeRow[1];
                const discount = discountRow[1];

                const orderItems = utils.sheet_to_json(
                  wb.Sheets[itemSheetName]
                );

                const items = map(orderItems, (item: any) => {
                  const name = item['品項名稱'];
                  const quantity = item['數量'];
                  const price = item['金額'];

                  if (!name) {
                    throw new Error('找不到品項名稱，請上傳正確的檔案');
                  }
                  if (!quantity) {
                    throw new Error('找不到數量，請上傳正確的檔案');
                  }
                  if (!price) {
                    throw new Error('找不到金額，請上傳正確的檔案');
                  }

                  return {
                    name: name,
                    quantity,
                    price,
                  };
                });

                form.setFieldsValue({
                  clientName,
                  clientTaxId,
                  deliveryDate,
                  deliveryAddress,
                  contactInfo,
                  items,
                  deliveryFee,
                  discount,
                });

                setTimeout(() => {
                  form.setFieldsValue({
                    items,
                  });
                });
              })
              .catch((e: any) => {
                message.error(e.message);
              });
            return false;
          }}
        >
          <Button type="primary">上傳訂單檔案</Button>
        </Upload>
        <a href="/企業訂單匯入範例.xlsx">下載企業訂單匯入範例</a>
      </div>
      <FormItem
        label="訂餐日期"
        name="deliveryDate"
        rules={[{ required: true, message: '請輸入訂餐日期' }]}
      >
        <DatePicker
          placeholder="請選擇訂餐日期"
          className="z-50 w-full"
          showTime
          showHour
          showMinute
          getPopupContainer={(triggerNode) => {
            return triggerNode.parentElement
              ? triggerNode.parentElement
              : triggerNode;
          }}
        />
      </FormItem>
      <div className="flex gap-2 *:flex-grow *:basis-0">
        <FormItem
          label="運費"
          name="deliveryFee"
          rules={[
            { required: true, message: '請輸入運費' },
            { type: 'integer', message: '運費必須是整數' },
          ]}
        >
          <InputNumber placeholder="請輸入運費" min={0} className="w-full" />
        </FormItem>
        <FormItem
          label="折扣(%)"
          name="discount"
          rules={[{ required: true, message: '請輸入折扣' }]}
        >
          <InputNumber
            placeholder="請輸入折扣"
            min={0}
            max={100}
            className="w-full"
            step={5}
          />
        </FormItem>
      </div>
      <div className="flex gap-2 *:flex-grow">
        <FormItem
          label="客戶名稱"
          name="clientName"
          rules={[{ required: true, message: '請輸入客戶名稱' }]}
        >
          <Input placeholder="請輸入客戶名稱" />
        </FormItem>
        <FormItem
          label="統一編號"
          name="clientTaxId"
          rules={[
            {
              required: true,
              message: '請輸入統一編號',
            },
            {
              pattern: /^[0-9]{8}$/,
              message: '統一編號格式不正確',
            },
          ]}
        >
          <Input placeholder="請輸入統一編號" />
        </FormItem>
      </div>
      <FormItem
        label="訂餐地址"
        name="deliveryAddress"
        rules={[{ required: true, message: '請輸入訂餐地址' }]}
      >
        <Input placeholder="請輸入訂餐地址" />
      </FormItem>
      <FormItem
        label="聯絡窗口"
        name="contactInfo"
        rules={[{ required: true, message: '請輸入聯絡窗口' }]}
      >
        <Input placeholder="請輸入聯絡窗口" />
      </FormItem>
      <FormList name="items">
        {(fields, { add, remove }) => {
          return (
            <>
              {map(fields, ({ key, name, ...restField }) => (
                <div key={key} className="flex flex-col">
                  <div>
                    <span className="font-bold">餐點 {name + 1}</span>
                    <Button type="link" onClick={() => remove(name)}>
                      刪除
                    </Button>
                  </div>
                  <div className="flex gap-2 *:flex-grow *:basis-0">
                    <ProviderSelectorContainer
                      isRequired
                      name={[name, 'providerId']}
                    />
                    <FormItem
                      {...restField}
                      name={[name, 'name']}
                      label="品項名稱"
                      rules={[{ required: true, message: '請輸入品項名稱' }]}
                      shouldUpdate
                    >
                      <Input
                        placeholder="請輸入品項名稱"
                        className="flex-grow"
                      />
                    </FormItem>
                  </div>
                  <div className="flex gap-2 *:flex-grow *:basis-0">
                    <FormItem
                      {...restField}
                      name={[name, 'price']}
                      label="金額"
                      className="flex-shrink-0"
                      rules={[{ required: true, message: '請輸入金額' }]}
                    >
                      <InputNumber
                        placeholder="請輸入金額"
                        min={1}
                        className="w-full"
                      />
                    </FormItem>
                    <FormItem
                      {...restField}
                      name={[name, 'quantity']}
                      label="數量"
                      className="flex-shrink-0"
                      rules={[{ required: true, message: '請輸入數量' }]}
                    >
                      <InputNumber
                        placeholder="數量"
                        min={1}
                        className="w-full"
                      />
                    </FormItem>
                  </div>
                </div>
              ))}
              <Button
                type="dashed"
                onClick={() => add()}
                block
                icon={<PlusOutlined />}
              >
                新增餐點
              </Button>
            </>
          );
        }}
      </FormList>
    </Form>
  );
};
